import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { Camera } from '@/models/camera';
import { Photo } from '@/models/photo';
import { State } from 'vuex-class';
import { TimelapseConfiguration } from '@/models/timelapseconfiguration';
import VueSlider from 'vue-slider-component';
import { Logger } from 'aws-amplify';
import S3Image from '../s3-image/S3Image.vue';
import PremiumOverlay from '../premiumoverlay/premiumoverlay.component.vue';
const logger = new Logger('TimelapseExclusion');

@Component({
  components: {
    VueSlider,
    PremiumOverlay,
    S3Image,
  },
})
export default class TimelapseExclusion extends Vue {
  @Prop({ required: true, type: Object }) private readonly camera!: Camera;
  @Prop({ required: true, type: Object }) private readonly value!: TimelapseConfiguration;
  @Prop() readonly imagesKey?: Photo[];
  @Prop({ required: true }) readonly isPremium!: boolean;

  @State('isLoading', { namespace: 'photo' }) readonly loadingPhoto!: boolean;
  @State('loadingTimelapse', { namespace: 'project' }) readonly loadingTimelapse!: boolean;

  private get model(): TimelapseConfiguration {
    return this.value;
  }

  private visible = true;
  private exposureRange: { min: number; max: number } = { min: 0, max: 250 };
  private gainRange: { min: number; max: number } = { min: 0, max: 1800 };
  private sharpRange: { min: number; max: number } = { min: 0, max: 60 };
  private personRange: { min: number; max: number } = { min: 0, max: 100 };
  private movementRange: { min: number; max: number } = { min: 0, max: 100 };
  private goutteRange: { min: number; max: number } = { min: 0, max: 100 };
  private localModel: TimelapseConfiguration = { ...this.value };

  public mounted(): void {
    this.localModel = { ...this.value };
    this.$nextTick(() => {
      this.getExposureRange();
      this.getSharpRange();
      this.getPersonRange();
      this.getGainRange();
      this.getMovementRange();
      this.getGoutteRange();
      this.$nextTick(() => {
        this.updateSlider();
        (this.$refs.exposureSlider as VueSlider)?.setValue(this.localModel.exposure);
        (this.$refs.gainSlider as VueSlider)?.setValue(this.localModel.gain);
        (this.$refs.sharpSlider as VueSlider)?.setValue(this.localModel.clearness);
        (this.$refs.personSlider as VueSlider)?.setValue(this.localModel.person);
        (this.$refs.movementSlider as VueSlider)?.setValue(this.localModel.movement);
        (this.$refs.goutteSlider as VueSlider)?.setValue(this.localModel.goutte);
      });
    });
  }

  public created(): void {
    this.localModel = { ...this.value };
    this.getExposureRange();
    this.getSharpRange();
    this.getPersonRange();
    this.getGainRange();
    this.getMovementRange();
    this.getGoutteRange();
  }

  private getExposureRange(): void {
    let min = Infinity;
    let max = -Infinity;

    this.imagesKey?.forEach((image: Photo) => {
      if (image.exposure !== undefined) {
        min = Math.min(min, image.exposure - 0.1);
        max = Math.max(max, image.exposure + 0.1);
      }
    });

    if (min === Infinity || max === -Infinity) {
      min = 0;
      max = 250;
    }

    this.exposureRange = {
      min: Number(min.toFixed(1)),
      max: Number(max.toFixed(1)),
    };

    // Mise à jour initiale des valeurs si nécessaire
    if (!this.value.exposure || !Array.isArray(this.value.exposure)) {
      this.updateExposure([this.exposureRange.min, this.exposureRange.max]);
    }
  }

  private updateExposure(newValue: [number, number]): void {
    const validatedValue: [number, number] = [
      Math.max(this.exposureRange.min, Math.min(this.exposureRange.max, Number(newValue[0]))),
      Math.max(this.exposureRange.min, Math.min(this.exposureRange.max, Number(newValue[1]))),
    ];

    const newModel = {
      ...this.value,
      exposure: validatedValue,
    };

    this.$emit('input', newModel);

    // Force la mise à jour du slider
    this.$nextTick(() => {
      const slider = this.$refs.exposureSlider as VueSlider;
      if (slider) {
        slider.setValue(validatedValue);
      }
    });
  }

  // Gestionnaire pour les changements d'input number
  private onExposureInputChange(index: number, value: number): void {
    const newValue = [...this.exposureValue] as [number, number];
    newValue[index] = Number(value);

    // Assurer que min <= max
    if (index === 0 && newValue[0] > newValue[1]) {
      newValue[1] = newValue[0];
    } else if (index === 1 && newValue[1] < newValue[0]) {
      newValue[0] = newValue[1];
    }

    this.updateExposure(newValue);
  }

  // Gestionnaire pour les changements du slider
  private onExposureSliderChange(value: [number, number]): void {
    this.updateExposure(value);
  }

  private get exposureValue(): [number, number] {
    if (!this.value.exposure || !Array.isArray(this.value.exposure)) {
      return [this.exposureRange.min, this.exposureRange.max];
    }
    return [
      Number(this.value.exposure[0]) || this.exposureRange.min,
      Number(this.value.exposure[1]) || this.exposureRange.max,
    ];
  }

  private getGainRange(): void {
    let min = Infinity;
    let max = -Infinity;

    // Calcul des min et max à partir des images
    this.imagesKey?.forEach((image: Photo) => {
      if (image.gainControl !== undefined) {
        min = Math.min(min, image.gainControl - 1);
        max = Math.max(max, image.gainControl + 1);
      }
    });

    // Si aucune valeur valide n'est trouvée, utiliser des valeurs par défaut
    if (min === Infinity || max === -Infinity) {
      min = 0; // Valeur par défaut minimale
      max = 1800; // Valeur par défaut maximale (cohérente avec gainRange initial)
    }

    this.gainRange = { min, max };

    // Mise à jour du modèle avec des valeurs dans la plage valide
    if (this.model.gain && this.model.gain.length === 2) {
      const currentMin = this.model.gain[0] !== undefined ? this.model.gain[0] : min;
      const currentMax = this.model.gain[1] !== undefined ? this.model.gain[1] : max;
      this.updateModel({
        ...this.model,
        gain: [
          Math.max(min, currentMin), // S'assurer que la valeur reste dans la plage
          Math.min(max, currentMax),
        ],
      });
    } else {
      this.updateModel({ ...this.model, gain: [min, max] });
    }
  }

  private getSharpRange(): void {
    let min = Infinity;
    let max = -Infinity;

    this.imagesKey?.forEach((image: Photo) => {
      if (image.sharpness !== undefined) {
        min = Math.min(min, image.sharpness - 0.1);
        max = Math.max(max, image.sharpness + 0.1);
      }
    });

    if (min === Infinity || max === -Infinity) {
      min = 0;
      max = 60;
    }

    this.sharpRange = { min, max };

    // 💥 CORRECTION : uniquement mettre une valeur par défaut si clearness est complètement vide
    if (!this.model.clearness || this.model.clearness.length !== 2) {
      const clearnessDefault: [number, number] = [min, max];
      this.updateModel({ ...this.model, clearness: clearnessDefault });
      this.localModel.clearness = clearnessDefault;
    }
  }

  private getMovementRange(): void {
    let min = 0; // Valeur par défaut
    let max = 100; // Valeur par défaut

    this.imagesKey?.forEach((image: Photo) => {
      if (image.movement !== undefined) {
        if (image.movement < min) {
          min = image.movement - 0.1;
        }
        if (image.movement > max) {
          max = image.movement + 0.1;
        }
      }
    });

    // Assurez-vous que min et max sont dans une plage acceptable
    min = Math.max(min, 0);
    max = Math.min(max, 100);

    this.movementRange = { min, max };
    console.log('Computed movementRange:', this.movementRange);

    // Mise à jour du modèle via la méthode updateModel
    if (this.model.movement && this.model.movement.length === 2) {
      // Vérifiez si les valeurs actuelles sont en dehors de la nouvelle plage
      const [currentMin, currentMax] = this.model.movement;
      const newMin = currentMin < min ? min : currentMin;
      const newMax = currentMax > max ? max : currentMax;

      this.updateModel({
        ...this.model,
        movement: [newMin !== undefined ? newMin : min, newMax !== undefined ? newMax : max],
      });
    } else {
      this.updateModel({ ...this.model, movement: [min, max] });
    }
  }

  private getGoutteRange(): void {
    let min = 0; // Valeur par défaut
    let max = 100; // Valeur par défaut

    this.imagesKey?.forEach((image: Photo) => {
      if (image.goutte !== undefined) {
        if (image.goutte < min) {
          min = image.goutte;
        }
        if (image.goutte > max) {
          max = image.goutte;
        }
      }
    });

    // Assurez-vous que min et max sont dans une plage acceptable
    min = Math.max(min, 0);
    max = Math.min(max, 100);

    this.goutteRange = { min, max };
    console.log('Computed goutteRange:', this.goutteRange);

    // Mise à jour du modèle via la méthode updateModel
    if (this.model.goutte && this.model.goutte.length === 2) {
      // Vérifiez si les valeurs actuelles sont en dehors de la nouvelle plage
      const [currentMin, currentMax] = this.model.goutte;
      const newMin = currentMin < min ? min : currentMin;
      const newMax = currentMax > max ? max : currentMax;

      this.updateModel({
        ...this.model,
        goutte: [newMin !== undefined ? newMin : min, newMax !== undefined ? newMax : max],
      });
    } else {
      this.updateModel({ ...this.model, goutte: [min, max] });
    }
  }

  private getPersonRange(): void {
    let min = Infinity;
    let max = -Infinity;

    this.imagesKey?.forEach((image: Photo) => {
      if (image.person !== undefined) {
        if (image.person < min) {
          min = image.person - 1;
        }
        if (image.person > max) {
          max = image.person + 1;
        }
      }
    });

    this.personRange = { min, max };

    // Mise à jour du modèle via la méthode updateModel
    if (this.model.person && this.model.person.length === 2) {
      this.updateModel({
        ...this.model,
        person: [
          this.model.person[0] !== undefined ? this.model.person[0] : min,
          this.model.person[1] !== undefined ? this.model.person[1] : max,
        ],
      });
    } else {
      this.updateModel({ ...this.model, person: [min, max] });
    }
  }

  private onSliderDragEnd(): void {
    const newModel = { ...this.localModel };
    this.$emit('input', newModel); // 🔥 Mise à jour vers l'extérieur (donc model)
  }

  private resetSliders() {
    if (this.model) {
      // Vérification pour exposure
      if (this.model.exposure[0] === 0 || this.model.exposure[1] === 250) {
        this.model.exposure = [this.exposureRange.min, this.exposureRange.max];
      }

      // Vérification pour gain
      if (this.model.gain[0] === 0 || this.model.gain[1] === 1800) {
        this.model.gain = [this.gainRange.min, this.gainRange.max];
      }

      // Vérification pour clearness
      if (this.model.clearness[0] === 0 || this.model.clearness[1] === 60) {
        this.model.clearness = [this.sharpRange.min, this.sharpRange.max];
      }

      // Mise à jour de person sans condition (car non spécifiée)
      if (this.model.person[0] === 0 || this.model.person[1] === 100) {
        this.model.person = [this.personRange.min, this.personRange.max];
      }

      if (this.model.movement[0] === 0 || this.model.movement[1] === 100) {
        this.model.movement = [this.movementRange.min, this.movementRange.max];
      }

      if (this.model.goutte[0] === 0 || this.model.goutte[1] === 100) {
        this.model.goutte = [this.goutteRange.min, this.goutteRange.max];
      }
      // Si nécessaire, émettre le modèle mis à jour
      this.$emit('input', this.model);
    }
  }

  private resetSliders1() {
    if (this.model) {
      // Stockez les nouvelles valeurs
      const newClearness = [Number(this.sharpRange.min.toFixed(2)), Number(this.sharpRange.max.toFixed(2))];

      // Mettez à jour le modèle avec les nouvelles valeurs
      const updatedModel = {
        ...this.model,
        exposure: [this.exposureRange.min, this.exposureRange.max],
        gain: [this.gainRange.min, this.gainRange.max],
        clearness: newClearness, // Utilisez les valeurs formatées
        person: [this.personRange.min, this.personRange.max],
        movement: [this.movementRange.min, this.movementRange.max],
        goutte: [this.goutteRange.min, this.goutteRange.max],
      };

      // Émettez d'abord le modèle mis à jour
      this.$emit('input', updatedModel);

      // Ensuite, forcez la mise à jour du slider clearness spécifiquement
      this.$nextTick(() => {
        const sharpSlider = this.$refs.sharpSlider as VueSlider;
        if (sharpSlider) {
          sharpSlider.setValue(newClearness);
        }
      });
    }
  }

  public onExposureMinSelected(value: number): void {
    this.$set(this.model.exposure, 0, value);
  }

  public onExposureMaxSelected(value: number): void {
    this.$set(this.model.exposure, 1, Number(value));
  }

  private updateModel(newModel: TimelapseConfiguration): void {
    this.$emit('input', newModel);
  }

  private updateSlider(): void {
    // Logs pour l'exposition
    if (this.model.exposure.length === 2) {
      const slider = this.$refs.exposureSlider as VueSlider;
      if (slider) {
        slider.setValue([this.model.exposure[0], this.model.exposure[1]]);
      } else {
        console.warn('Exposure slider reference is undefined');
      }
    }
    // Logs pour le gain
    if (this.model.gain.length === 2) {
      const slider = this.$refs.gainSlider as VueSlider;
      if (slider) {
        slider.setValue([this.model.gain[0], this.model.gain[1]]);
      } else {
        console.warn('Gain slider reference is undefined');
      }
    }
    // Logs pour la netteté (clearness)
    if (this.model.clearness?.length === 2) {
      const slider = this.$refs.sharpSlider as VueSlider;
      if (slider) {
        try {
          slider.setValue([Number(this.model.clearness[0]), Number(this.model.clearness[1])]);
        } catch (error) {
          console.warn('Error updating sharp slider:', error);
        }
      }
    }
    // Logs pour les personnes
    if (this.model.person.length === 2) {
      const slider = this.$refs.personSlider as VueSlider;
      if (slider) {
        slider.setValue([this.model.person[0], this.model.person[1]]);
      } else {
        console.warn('Clearness slider reference is undefined');
      }
    }
    // Mise à jour du movementSlider
    if (this.model.movement.length === 2) {
      const slider = this.$refs.movementSlider as VueSlider;
      if (slider) {
        slider.setValue([this.model.movement[0], this.model.movement[1]]);
        console.log('Movement slider updated with:', this.model.movement);
      } else {
        console.warn('Movement slider reference is undefined');
      }
    }

    if (this.model.goutte.length === 2) {
      const slider = this.$refs.goutteSlider as VueSlider;
      if (slider) {
        slider.setValue([this.model.goutte[0], this.model.goutte[1]]);
        console.log('Movement slider updated with:', this.model.goutte);
      } else {
        console.warn('Movement slider reference is undefined');
      }
    }
  }

  @Watch('loadingPhoto')
  public onLoadingPhoto(val: boolean, oldVal: boolean): void {
    logger.info('loadingPhoto', { val, oldVal, loadingTimelapse: this.loadingTimelapse });
    if (oldVal && !val && !this.loadingTimelapse) {
      this.getExposureRange();
      this.getGainRange();
      this.getSharpRange();
      this.getPersonRange();
      this.getMovementRange();
      this.getGoutteRange();
      this.resetSliders();
    }
  }

  @Watch('exposureRange', { deep: true, immediate: true })
  onExposureRangeChanged(newRange: { min: number; max: number }) {
    const modelCopy = JSON.parse(JSON.stringify(this.model));
    modelCopy.exposure[0] = newRange.min.toFixed(1);
    modelCopy.exposure[1] = newRange.max.toFixed(1);
  }

  @Watch('gainRange', { deep: true, immediate: true })
  onGainRangeChanged(newRange: { min: number; max: number }) {
    const modelCopy = JSON.parse(JSON.stringify(this.model));
    modelCopy.gain[0] = parseFloat(newRange.min.toFixed(1));
    modelCopy.gain[1] = parseFloat(newRange.max.toFixed(1));
  }

  @Watch('sharpRange', { deep: true, immediate: true })
  onSharpRangeChanged(newRange: { min: number; max: number }) {
    if (this.model && this.model.clearness) {
      const newMin = parseFloat(newRange.min.toFixed(2));
      const newMax = parseFloat(newRange.max.toFixed(2));

      // Only update if values are outside the new range
      if (this.model.clearness[0] < newMin || this.model.clearness[1] > newMax) {
        const newModel = { ...this.model, clearness: [newMin, newMax] };
        this.updateModel(newModel);

        // Update slider directly if needed
        const slider = this.$refs.sharpSlider as VueSlider;
        if (slider) {
          slider.setValue([newMin, newMax]);
        }
      }
    }
  }

  @Watch('personRange', { deep: true, immediate: true })
  onPersonRangeChanged(newRange: { min: number; max: number }) {
    const modelCopy = JSON.parse(JSON.stringify(this.model));
    modelCopy.person[0] = parseFloat(newRange.min.toFixed(1));
    modelCopy.person[1] = parseFloat(newRange.max.toFixed(1));
  }

  @Watch('movementRange', { deep: true, immediate: true })
  onMovementRangeChanged(newRange: { min: number; max: number }) {
    const currentMin = this.model.movement[0];
    const currentMax = this.model.movement[1];
    const newMin = parseFloat(newRange.min.toFixed(1));
    const newMax = parseFloat(newRange.max.toFixed(1));

    // Vérifier si les valeurs actuelles sont en dehors de la nouvelle plage
    let needsUpdate = false;
    if (currentMin < newMin || currentMax > newMax) {
      needsUpdate = true;
    }

    // Mettre à jour uniquement si nécessaire
    if (needsUpdate) {
      const newModel = { ...this.model, movement: [newMin, newMax] };
      this.updateModel(newModel);
    }
  }

  @Watch('goutteRange', { deep: true, immediate: true })
  onGoutteRangeChanged(newRange: { min: number; max: number }) {
    const modelCopy = JSON.parse(JSON.stringify(this.model));
    modelCopy.goutte[0] = parseFloat(newRange.min.toFixed(1));
    modelCopy.goutte[1] = parseFloat(newRange.max.toFixed(1));
  }

  @Watch('value', { immediate: true, deep: true })
  onValueChanged(newValue: TimelapseConfiguration) {
    this.localModel = { ...newValue };
  }
}
